import React, { useContext, useEffect, useState } from 'react';
import { Alert, Box, MenuItem, Select, CircularProgress, Grid, Typography } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { styled, alpha } from '@mui/material/styles';
import { GlobalStateContext } from '../../context/empcontext';
import { StyledInput, Label, grey, SELECTED_MENU_PROPS } from '../StyledComponents/StyledComponent';
import {
    GET_REPORTING_MANAGER_OPTIONS, GET_BRANCHES, GET_LOCATIONS, GET_TEAM_HEADS,
    GET_DEPARTMENTS, GET_DESIGNATIONS,
} from '../../api';
import { reconcileOrganizationSelection } from './organizationSelection';
import RelationalAutocomplete from './RelationalAutocomplete';

const CustomSelect = styled(({ MenuProps, ...props }) => (
    <Select MenuProps={{ ...SELECTED_MENU_PROPS, ...MenuProps }} {...props} />
))(({ theme }) => ({
    width: '100%',
    height: '40px',
    backgroundColor: theme.palette.mode === 'dark' ? alpha(grey[900], 0.4) : alpha('#fff', 0.6),
    backdropFilter: 'blur(8px)',
    borderRadius: '10px',
    border: `1px solid ${theme.palette.mode === 'dark' ? alpha(grey[700], 0.5) : alpha(grey[200], 0.5)}`,
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    boxShadow: `0 4px 6px ${alpha('#000', 0.02)}`,
    transition: 'all 0.2s ease',
    '&:hover': {
        border: '1px solid #3DAC5A',
    },
    '&.Mui-focused': {
        border: '1px solid #3DAC5A',
        backgroundColor: '#fff',
    }
}));

const JoiningInfo = () => {
    const { formData, setFormData } = useContext(GlobalStateContext);
    const [dateOfJoining, setDateOfJoining] = useState(formData.date_of_joining ? dayjs(formData.date_of_joining) : dayjs());
    const [, setConfirmDate] = useState(formData.confirmation_date ? dayjs(formData.confirmation_date) : dayjs());
    const [managers, setManagers] = useState([]);
    const [branches, setBranches] = useState([]);
    const [locations, setLocations] = useState([]);
    const [teamHeads, setTeamHeads] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);
    const [loadingMaster, setLoadingMaster] = useState(false);
    const [organizationError, setOrganizationError] = useState('');

    // Helper to ensure the current value is always an option (prevents MUI
    // warnings). A legacy value that doesn't match any master-list entry is
    // surfaced right after the placeholder — not buried at the bottom — with a
    // disabled hint line explaining why. The hint is its OWN MenuItem, never
    // the selectable one: MUI's Select renders the selected MenuItem's
    // children verbatim in the closed box, so if the explanation lived inside
    // the selectable item itself, it would leak into the field's display text.
    // `masterOnly` (Branch): the legacy value is still shown so HR can see what
    // is stored, but it is disabled — HR can only pick a master entry. A
    // non-master branch hides the employee from branch-scoped HR (HRP #55718).
    const renderSafeOptions = (options, currentValue, placeholder, masterOnly = false) => {
        const hasValue = options.some(opt => (opt.name || opt) === currentValue);
        return [
            <MenuItem key="placeholder" value="" disabled>{placeholder}</MenuItem>,
            currentValue && !hasValue && (
                <MenuItem key="fallback-hint" disabled sx={{ fontSize: '0.72rem', color: masterOnly ? 'error.main' : 'text.secondary' }}>
                    {masterOnly ? 'Not an official branch — please choose one below:' : 'Current value (not in master list):'}
                </MenuItem>
            ),
            currentValue && !hasValue && (
                <MenuItem key="fallback" value={currentValue} disabled={masterOnly}>{currentValue}</MenuItem>
            ),
            ...options.map((opt, i) => (
                <MenuItem key={i} value={opt.name || opt}>{opt.name || opt}</MenuItem>
            )),
        ];
    };

    useEffect(() => {
        const fetchMasterData = async () => {
            setLoadingMaster(true);
            try {
                const [mgrs, brs, locs, ths, depts, desigs] = await Promise.all([
                    GET_REPORTING_MANAGER_OPTIONS(),
                    GET_BRANCHES(),
                    GET_LOCATIONS(),
                    GET_TEAM_HEADS(),
                    GET_DEPARTMENTS(),
                    GET_DESIGNATIONS(),
                ]);
                setManagers(mgrs || []);
                setBranches(brs || []);
                setLocations(locs || []);
                setTeamHeads(ths || []);
                setDepartments((depts || []).filter((item) => item.is_active));
                setDesignations((desigs || []).filter((item) => item.is_active));
                setOrganizationError('');
            } catch (error) {
                console.error('Error fetching master data:', error);
                setOrganizationError('Organization and reporting-manager options could not be loaded. Please retry.');
            } finally {
                setLoadingMaster(false);
            }
        };
        fetchMasterData();
    }, []);

    // Candidate records contain legacy organization names, while employee
    // creation requires tenant-owned master IDs. Resolve exact, unique matches
    // as soon as this tenant's active master data is available.
    useEffect(() => {
        if (loadingMaster || (!departments.length && !designations.length)) return;
        setFormData((previous) => reconcileOrganizationSelection(
            previous, departments, designations
        ));
    }, [departments, designations, loadingMaster, setFormData]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleDateChange = (newDate, field) => {
        const formattedDate = dayjs(newDate).format('YYYY-MM-DD');
        if (field === 'date_of_joining') {
            setDateOfJoining(dayjs(newDate));
            setFormData(prev => ({ ...prev, date_of_joining: formattedDate }));
        } else if (field === 'confirmation_date') {
            setConfirmDate(dayjs(newDate));
            setFormData(prev => ({ ...prev, confirmation_date: formattedDate }));
        }
    };

    const handleDepartmentChange = (selected) => {
        setFormData((previous) => ({
            ...previous,
            department_id: selected?.id || null,
            department: selected?.name || '',
            designation_id: null,
            designation: '',
        }));
    };

    const handleDesignationChange = (selected) => {
        setFormData((previous) => ({
            ...previous,
            designation_id: selected?.id || null,
            designation: selected?.name || '',
        }));
    };

    const handleManagerChange = (selected) => {
        setFormData((previous) => ({
            ...previous,
            reporting_manager_id: selected?.id || null,
            reporting_manager: selected?.name || '',
            manager_id: selected?.employee_no || '',
        }));
    };

    const handleBranchChange = (selected) => {
        setFormData((previous) => ({
            ...previous,
            branch_id: selected?.id || null,
            branch_name: selected?.name || '',
        }));
    };

    const availableDesignations = designations.filter(
        (item) => String(item.department) === String(formData.department_id)
    );

    return (
        <Box sx={{ position: 'relative' }}>
            {organizationError && <Alert severity="error" sx={{ mb: 2 }}>{organizationError}</Alert>}
            {loadingMaster && (
                <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1, bgcolor: 'rgba(255,255,255,0.4)', backdropFilter: 'blur(4px)', borderRadius: '16px' }}>
                    <CircularProgress size={32} color="success" />
                </Box>
            )}
            <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                    <Label>Employment Status *</Label>
                    <CustomSelect
                        name="employment_status"
                        value={formData.employment_status || ''}
                        onChange={handleInputChange}
                        displayEmpty
                    >
                        {renderSafeOptions(['Probation', 'Confirmed'], formData.employment_status, 'Select Status')}
                    </CustomSelect>
                </Grid>

                <Grid item xs={12} sm={4}>
                    <Label>Status *</Label>
                    <CustomSelect
                        name="status"
                        value={formData.status || ''}
                        onChange={handleInputChange}
                        displayEmpty
                    >
                        {renderSafeOptions(['Active', 'Inactive'], formData.status, 'Select Status')}
                    </CustomSelect>
                </Grid>

                <Grid item xs={12} sm={4}>
                    <Label>Date of Joining *</Label>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            value={dateOfJoining}
                            onChange={(val) => handleDateChange(val, 'date_of_joining')}
                            format="DD/MM/YYYY"
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    sx: {
                                        '& .MuiOutlinedInput-root': {
                                            height: '40px',
                                            borderRadius: '10px',
                                            bgcolor: alpha('#fff', 0.6),
                                            '& fieldset': { border: `1px solid ${alpha(grey[200], 0.5)}` },
                                            '&:hover fieldset': { borderColor: '#3DAC5A' },
                                            '&.Mui-focused fieldset': { borderColor: '#3DAC5A' },
                                        }
                                    }
                                }
                            }}
                        />
                    </LocalizationProvider>
                </Grid>

                <Grid item xs={12} sm={4}>
                    <Label>Designation</Label>
                    <RelationalAutocomplete
                        ariaLabel="Designation"
                        options={availableDesignations}
                        valueId={formData.designation_id}
                        legacyLabel={formData.designation}
                        onChange={handleDesignationChange}
                        loading={loadingMaster}
                        disabled={loadingMaster || !formData.department_id || availableDesignations.length === 0}
                        placeholder="Search Designation"
                        emptyText={formData.department_id
                            ? 'No active designations for this department'
                            : 'Select a Department first'}
                    />
                </Grid>

                <Grid item xs={12} sm={4}>
                    <Label>Department</Label>
                    <RelationalAutocomplete
                        ariaLabel="Department"
                        options={departments}
                        valueId={formData.department_id}
                        legacyLabel={formData.department}
                        onChange={handleDepartmentChange}
                        loading={loadingMaster}
                        disabled={loadingMaster || departments.length === 0}
                        placeholder="Search Department"
                        emptyText="No active departments configured"
                    />
                    {!loadingMaster && departments.length === 0 && !organizationError && (
                        <Typography variant="caption" color="text.secondary">
                            Create a Department in Master Data before assigning it.
                        </Typography>
                    )}
                </Grid>

                <Grid item xs={12} sm={4}>
                    <Label>Reporting Manager</Label>
                    <RelationalAutocomplete
                        ariaLabel="Reporting Manager"
                        options={managers}
                        valueId={formData.reporting_manager_id}
                        legacyLabel={formData.reporting_manager}
                        onChange={handleManagerChange}
                        loading={loadingMaster}
                        disabled={loadingMaster || managers.length === 0}
                        placeholder="Search Reporting Manager"
                        emptyText="No active employees available"
                        getOptionLabel={(manager) => `${manager.name} (${manager.employee_no})`}
                    />
                    {!loadingMaster && managers.length === 0 && !organizationError && (
                        <Typography variant="caption" color="text.secondary">
                            Add an active employee before assigning a reporting manager.
                        </Typography>
                    )}
                </Grid>

                <Grid item xs={12} sm={4}>
                    <Label>Branch Name *</Label>
                    <RelationalAutocomplete
                        ariaLabel="Branch"
                        options={branches}
                        valueId={formData.branch_id}
                        legacyLabel={formData.branch_name}
                        onChange={handleBranchChange}
                        loading={loadingMaster}
                        disabled={loadingMaster || branches.length === 0}
                        placeholder="Search Branch"
                        emptyText="No branches configured"
                    />
                </Grid>

                <Grid item xs={12} sm={4}>
                    <Label>Division</Label>
                    <StyledInput
                        name="division"
                        value={formData.division || ''}
                        onChange={handleInputChange}
                        placeholder="Enter Division"
                    />
                </Grid>

                <Grid item xs={12} sm={4}>
                    <Label>Region / Location *</Label>
                    <CustomSelect
                        name="location"
                        value={formData.location || ''}
                        onChange={handleInputChange}
                        displayEmpty
                    >
                        {renderSafeOptions(locations, formData.location, 'Select Location')}
                    </CustomSelect>
                </Grid>

                <Grid item xs={12} sm={4}>
                    <Label>Attendance Schema *</Label>
                    <CustomSelect
                        name="attendance_schema"
                        value={formData.attendance_schema || ''}
                        onChange={handleInputChange}
                        displayEmpty
                    >
                        {renderSafeOptions(['8.30 AM - 5.30 PM', '9.00 AM - 6.30 PM', '8.00 AM - 5.00 PM', '09:00AM - 06:00PM scheme'], formData.attendance_schema, 'Select Schema')}
                    </CustomSelect>
                </Grid>

                <Grid item xs={12} sm={4}>
                    <Label>Grade</Label>
                    <StyledInput
                        name="grade"
                        value={formData.grade || ''}
                        onChange={handleInputChange}
                        placeholder="Enter Grade"
                    />
                </Grid>

                <Grid item xs={12} sm={4}>
                    <Label>Level Cost Center</Label>
                    <StyledInput
                        name="level_cost_center"
                        value={formData.level_cost_center || ''}
                        onChange={handleInputChange}
                        placeholder="Enter Level Cost Center"
                    />
                </Grid>

                <Grid item xs={12} sm={4}>
                    <Label>Team Head *</Label>
                    <CustomSelect
                        name="team_head"
                        value={formData.team_head || ''}
                        onChange={handleInputChange}
                        displayEmpty
                    >
                        {renderSafeOptions(teamHeads, formData.team_head, 'Select Team Head')}
                    </CustomSelect>
                </Grid>
            </Grid>
        </Box>
    );
};

export default JoiningInfo;
